// Hampshire Holiday Parks booking strategy.
//
// Roomstay's checkout SPA (https://hampshireholidayparks.co.nz/{slug}-checkout/)
// has two relevant URL shapes:
//
//   * Booking deep-link: `#/step/room/?arrive=YYYY-MM-DD&depart=YYYY-MM-DD&adults=N`
//     — when arrive/depart are present the SPA lands on the room-picker step
//     with dates prefilled. When they are missing the SPA bounces to
//     `#/step/date/`, defeating the deep-link intent.
//
//   * Marketing page: `https://hampshireholidayparks.co.nz/park/{marketing-slug}/`
//     — the operator-authored overview page with photos, layout map, and a
//     plain-language description of every site / cabin type. This is the
//     right destination for the request form's "View map" button because
//     the booking SPA without dates is unhelpful for a "what does this
//     park have?" preview.
//
// A park that has moved to SynXis (book.hampshireholidayparks.co.nz) gets
// a site.reserveUrl on that host. SynXis reads ordinary query parameters,
// so the stay goes in the query string rather than the hash; Roomstay's
// hash parameters would be lost in the server-side redirect.
//
// The marketing slug is captured per-park during scrape (the API's
// `hotel.hotelUrl`) and surfaced as `site.extraAttributes.parkInfoUrl`,
// rather than reconstructed from `checkoutSlug` — the slugs don't map
// cleanly (Roomstay uses `ohope` while the marketing site uses
// `ohope-beach-top10-holiday-park`).

import { format } from "date-fns";
import { resolveCheckout, resolveMinCapacity } from "./_context.js";

const SYNXIS_HOST = "book.hampshireholidayparks.co.nz";

function formatHampshireDate(date) {
  return format(date, "yyyy-MM-dd");
}

export default {
  buildBookingUrl(ctx) {
    const { site, arrivalDate } = ctx;
    const checkout = resolveCheckout(ctx);
    const adults = resolveMinCapacity(ctx);

    const url = new URL(site.reserveUrl);
    if (url.host === SYNXIS_HOST) {
      url.searchParams.set("arrive", formatHampshireDate(arrivalDate));
      url.searchParams.set("depart", formatHampshireDate(checkout));
      url.searchParams.set("adult", String(adults));
      return url.toString();
    }
    // Roomstay routes via the URL hash, so URLSearchParams doesn't help
    // — manipulate the hash by hand.
    const [path, query = ""] = (url.hash || "#/step/room/").split("?");
    const params = new URLSearchParams(query);
    params.set("arrive", formatHampshireDate(arrivalDate));
    params.set("depart", formatHampshireDate(checkout));
    params.set("adults", String(adults));
    if (site.extraAttributes?.roomCode != null) {
      params.set("room", String(site.extraAttributes.roomCode));
    }
    url.hash = `${path}?${params.toString()}`;
    return url.toString();
  },

  buildPreviewUrl(site) {
    // Prefer the marketing page; fall back to the booking SPA only if a
    // future park is added without a hotelUrl in the API response.
    return site.extraAttributes?.parkInfoUrl ?? site.reserveUrl;
  },

  describeBookingInputs() {
    return {
      inputs: {
        arrivalDate: {
          status: "required",
          notes:
            "Encoded as &arrive=YYYY-MM-DD in the Roomstay SPA hash, or in the query string on SynXis.",
        },
        departureDate: {
          status: "required",
          notes:
            "Encoded as &depart=YYYY-MM-DD; resolved from arrival + minStayLength when not supplied.",
        },
        ref: {
          status: "ignored",
          notes:
            "site.reserveUrl already targets the per-park checkout SPA; ref is not encoded.",
        },
        partySize: {
          status: "defaulted",
          defaultValue: 1,
          notes:
            "Encoded as &adults=<N> (Roomstay) or &adult=<N> (SynXis); resolves from AvailabilityRequest.minCapacity, then partySize, then 1.",
        },
      },
    };
  },
};
